import * as React from 'react';

import {
  Badge,
  Button,
  DialogClosingConfirmation,
  EntityDetails,
  Loading,
  LocalizedDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft } from 'lucide-react';
import { useLocation } from 'wouter';

import * as state from '../../_state/brokeredOrderDetail.state';
import {
  $discardBaseline,
  $hasUnsavedChanges,
  $resetBrokeredOrderFormToOrder,
  BrokeredOrderFormState,
} from '../../_state/brokeredOrderForm.state';
import { usePopulateBrokeredOrderForm } from '../../hooks/usePopulateBrokeredOrderForm';
import { useTranslations } from '../../i18n/useTranslations';
import { brokerageRoutes } from '../../index';
import { OrderTab } from '../../types/brokeredOrder';
import { colorByBrokeOrderStatus } from '../../utils/status';
import { BrokerageLayout } from '../_layouts/BrokerageLayout';

import { BrokeredOrderActionHeader } from './BrokeredOrderActionHeader';
import { BrokeredOrderTabLinkedOrders } from './BrokeredOrderTabLinkedOrders';
import { BrokeredOrderTabMaterials } from './BrokeredOrderTabMaterials';
import { BrokeredOrderTabOverview } from './BrokeredOrderTabOverview';

import { ModuleLayout } from '~/shared/layouts/ModuleLayout';

export const BrokeredOrderDetailView = () => {
  const t = useTranslations();
  const [, navigate] = useLocation();

  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<OrderTab | null>(null);

  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);
  const { data: order, refetch } = useAtomValue(
    state.$remoteBrokeredOrderDetail,
  );
  const isLoading = useAtomValue(state.$isBrokeredOrderLoading);
  const error = useAtomValue(state.$brokeredOrderError);

  const [, resetForm] = useAtom($resetBrokeredOrderFormToOrder);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardBaseline);
  const [tab, setTab] = useAtom(state.$tab);

  const updateOrder = useSetAtom(state.$updateBrokeredOrder);

  const toast = useToast();

  usePopulateBrokeredOrderForm({ order });

  const activeTab = tab || 'overview';

  const handleBack = React.useCallback(() => {
    navigate(brokerageRoutes.orders.path);
  }, [navigate]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.orders.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: BrokeredOrderFormState) => {
      if (!order?.id || !data) return;

      try {
        setIsUpdating(true);
        await updateOrder({
          id: order.id,
          notes: data.notes.trim() || null,
        });

        setDiscardBaseline(data);
        toast.success(t.orders.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.orders.toast.updateError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [order?.id, updateOrder, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab as OrderTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm, setTab]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  if (isLoading) {
    return (
      <ModuleLayout title={t.orders.detail.title}>
        <Loading />
      </ModuleLayout>
    );
  }

  if (error || !order) {
    return (
      <ModuleLayout title={t.orders.detail.title}>
        <div className="p-6">
          <div className="text-failure">
            {t.common.errors.notFound(t.orders.detail.title)}:{' '}
            {error?.message ?? ''}
          </div>
          <Button variant="outline" onPress={handleBack} className="mt-4">
            <ArrowLeft className="mr-2 size-4" />
            {t.orders.detail.backButton}
          </Button>
        </div>
      </ModuleLayout>
    );
  }

  return (
    <BrokerageLayout className="@BrokeredOrderDetailView">
      <EntityDetails
        onChangeTab={(newTab) => setTab(newTab as OrderTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={t.orders.detail.orderTitle(order.orderNumber)}
        subtitle={
          <>
            {t.orders.detail.createdAt}{' '}
            <LocalizedDate date={order.timestamps.createdAt} />
          </>
        }
        badges={
          <Badge color={colorByBrokeOrderStatus[order.status]} size="small">
            {order.status}
          </Badge>
        }
        headerActions={
          <BrokeredOrderActionHeader order={order} onRefetch={refetch} />
        }
        tabs={{
          overview: {
            title: t.orders.detail.tabs.overview,
            content: () => (
              <BrokeredOrderTabOverview
                order={order}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          orders: {
            title: t.orders.detail.tabs.linkedOrders,
            content: () => (
              <BrokeredOrderTabLinkedOrders order={order} onRefetch={refetch} />
            ),
          },
          materials: {
            title: t.orders.detail.tabs.materials,
            content: () => (
              <BrokeredOrderTabMaterials order={order} onRefetch={refetch} />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.orders.detail.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </BrokerageLayout>
  );
};
